export type VizAccent = 'indigo' | 'teal' | 'amber' | 'violet' | 'rose' | 'cyan' | 'blue' | 'green' | 'pink';

/** Resolved color tokens for an accent: a solid hue plus soft surface/border tints. */
export interface VizAccentTokens {
    solid: string;
    soft: string;
    border: string;
    text: string;
    rgb: [number, number, number];
}

/**
 * Source hex values per accent. These are emitted as `--bm-viz-<name>-*` CSS
 * variables by {@link vizAccentVars} (wired into the theme's CSSVariablesResolver),
 * and {@link accentTokens} hands out the var() references — so a future theme can
 * re-skin every data-viz accent by overriding one block of variables.
 */
const PALETTE: Record<VizAccent, VizAccentTokens> = {
    indigo: { solid: '#4f46e5', soft: 'rgba(79, 70, 229, 0.10)', border: 'rgba(79, 70, 229, 0.22)', text: '#4f46e5', rgb: [79, 70, 229] },
    teal: { solid: '#0d9488', soft: 'rgba(13, 148, 136, 0.10)', border: 'rgba(13, 148, 136, 0.22)', text: '#0f766e', rgb: [13, 148, 136] },
    amber: { solid: '#d97706', soft: 'rgba(217, 119, 6, 0.10)', border: 'rgba(217, 119, 6, 0.22)', text: '#b45309', rgb: [217, 119, 6] },
    violet: { solid: '#7c3aed', soft: 'rgba(124, 58, 237, 0.10)', border: 'rgba(124, 58, 237, 0.22)', text: '#6d28d9', rgb: [124, 58, 237] },
    rose: { solid: '#e11d48', soft: 'rgba(225, 29, 72, 0.10)', border: 'rgba(225, 29, 72, 0.22)', text: '#be123c', rgb: [225, 29, 72] },
    cyan: { solid: '#0891b2', soft: 'rgba(8, 145, 178, 0.10)', border: 'rgba(8, 145, 178, 0.22)', text: '#0e7490', rgb: [8, 145, 178] },
    blue: { solid: '#2563eb', soft: 'rgba(37, 99, 235, 0.10)', border: 'rgba(37, 99, 235, 0.22)', text: '#1d4ed8', rgb: [37, 99, 235] },
    green: { solid: '#16a34a', soft: 'rgba(22, 163, 74, 0.10)', border: 'rgba(22, 163, 74, 0.22)', text: '#15803d', rgb: [22, 163, 74] },
    pink: { solid: '#db2777', soft: 'rgba(219, 39, 119, 0.10)', border: 'rgba(219, 39, 119, 0.22)', text: '#be185d', rgb: [219, 39, 119] },
};

const ACCENTS = Object.keys(PALETTE) as VizAccent[];

/** All `--bm-viz-<name>-*` variables — spread into the theme's CSSVariablesResolver. */
export function vizAccentVars(): Record<string, string> {
    const vars: Record<string, string> = {};
    for (const name of ACCENTS) {
        const t = PALETTE[name];
        vars[`--bm-viz-${name}-solid`] = t.solid;
        vars[`--bm-viz-${name}-soft`] = t.soft;
        vars[`--bm-viz-${name}-border`] = t.border;
        vars[`--bm-viz-${name}-text`] = t.text;
        vars[`--bm-viz-${name}-rgb`] = t.rgb.join(', ');
    }
    return vars;
}

/** Var-backed token set per accent, so consumers restyle with the theme. */
const VAR_TOKENS: Record<VizAccent, VizAccentTokens> = Object.fromEntries(
    ACCENTS.map((name) => [
        name,
        {
            solid: `var(--bm-viz-${name}-solid)`,
            soft: `var(--bm-viz-${name}-soft)`,
            border: `var(--bm-viz-${name}-border)`,
            text: `var(--bm-viz-${name}-text)`,
            rgb: PALETTE[name].rgb,
        },
    ]),
) as Record<VizAccent, VizAccentTokens>;

export function accentTokens(accent: VizAccent): VizAccentTokens {
    return VAR_TOKENS[accent];
}

/** Composes the accent hue at a given alpha — for heatmap intensity without fading text. */
export function accentRgba(accent: VizAccent, alpha: number): string {
    return `rgba(var(--bm-viz-${accent}-rgb), ${alpha.toFixed(3)})`;
}
